import { useEffect, useRef, useState, type FormEvent } from "react";
import {
  API_ROUTES,
  BET_KIND_HELP,
  BET_KIND_LABELS,
  BJ,
  HORSES,
  KN,
  clampKnBet,
  fmtChips,
  formatKnBet,
  getBetOdds,
  horseById,
  horsesNeeded,
  isKnBetWinner,
  koniePayout,
  type BjLeaderboardRow,
  type BjWallet,
  type KnBetKind,
  type KnPublicBet,
} from "@ziolo/shared";
import { Button, Card } from "@ziolo/ui";
import { apiRequest, errorMessage } from "../lib/apiClient.js";
import { useSession } from "../session/SessionProvider.js";
import { ShopPanel } from "../games/shop/ShopPanel.js";
import { KonieHelp } from "../games/konie/KonieHelp.js";
import { RaceTrack } from "../games/konie/RaceTrack.js";
import { useKonieSocket } from "../games/konie/useKonieSocket.js";
import { CasinoOnPageAdmin } from "../components/admin/CasinoAdminTools.js";
import "../styles/konie.css";
import "../styles/shop.css";

function formatHms(ms: number): string {
  const total = Math.max(0, Math.ceil(ms / 1000));
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const seconds = total % 60;
  return `${hours}:${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
}

function useCountdown(deadline: number | null): number {
  const [left, setLeft] = useState(0);
  useEffect(() => {
    if (deadline === null) {
      setLeft(0);
      return;
    }
    const tick = () => setLeft(Math.max(0, Math.ceil((deadline - Date.now()) / 1000)));
    tick();
    const id = window.setInterval(tick, 250);
    return () => window.clearInterval(id);
  }, [deadline]);
  return left;
}

function useLeaderboard(refreshToken: number) {
  const [rows, setRows] = useState<BjLeaderboardRow[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const load = () => {
      apiRequest<BjLeaderboardRow[]>(API_ROUTES.blackjack.leaderboard)
        .then((next) => {
          if (!cancelled) {
            setRows(next);
            setError(null);
          }
        })
        .catch((cause: unknown) => {
          if (!cancelled) setError(errorMessage(cause));
        });
    };
    load();
    const id = window.setInterval(load, 30_000);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, [refreshToken]);

  return { rows, error };
}

function useWallet(enabled: boolean, refreshToken: number) {
  const [wallet, setWallet] = useState<BjWallet | null>(null);

  useEffect(() => {
    if (!enabled) {
      setWallet(null);
      return;
    }
    let cancelled = false;
    apiRequest<BjWallet>(API_ROUTES.blackjack.wallet)
      .then((next) => {
        if (!cancelled) setWallet(next);
      })
      .catch(() => {
        // Socket session fills this in.
      });
    return () => {
      cancelled = true;
    };
  }, [enabled, refreshToken]);

  return wallet;
}

function chipLabel(amount: number): string {
  if (amount >= 1000) return `${amount / 1000}k`;
  return String(amount);
}

const KIND_TABS: KnBetKind[] = ["win", "place", "show", "exacta", "quinella", "trifecta"];

export function KoniePage() {
  const { user, restoring } = useSession();
  const enabled = Boolean(user) && !restoring;
  const {
    table,
    session,
    chat,
    notice,
    connected,
    error,
    walletEpoch,
    race,
    join,
    leave,
    placeBet,
    clearBets,
    sendChat,
    claimBonus,
  } = useKonieSocket(enabled);
  const [adminTick, setAdminTick] = useState(0);
  const board = useLeaderboard(walletEpoch + adminTick);
  const wallet = useWallet(enabled, walletEpoch);
  const [chip, setChip] = useState<number>(KN.DEFAULT_BET);
  const [kind, setKind] = useState<KnBetKind>("win");
  const [picks, setPicks] = useState<(number | null)[]>([null]);
  const [chatDraft, setChatDraft] = useState("");
  const [chatOpen, setChatOpen] = useState(false);
  const [rankOpen, setRankOpen] = useState(false);
  const [shopOpen, setShopOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const chatEnd = useRef<HTMLDivElement | null>(null);

  const bettingLeft = useCountdown(table.phase === "betting" ? table.bettingEndsAt : null);
  const raceLeft = useCountdown(table.phase === "racing" ? table.raceEndsAt : null);
  const resultLeft = useCountdown(table.phase === "results" ? table.resultEndsAt : null);
  const bonusLeft = useCountdown(session?.canClaim ? null : (session?.nextAvailableAt ?? wallet?.nextBonusAt ?? null));

  const userId = user?.id ?? 0;
  const me = table.players.find((player) => player.userId === userId);
  const seated = Boolean(me) || Boolean(session?.seated);
  const chips = me?.chips ?? session?.chips ?? wallet?.chips ?? 0;
  const keys = me?.keys ?? session?.keys ?? wallet?.keys ?? 0;
  const myBets = table.bets.filter((bet) => bet.userId === userId);
  const myWin = table.winners.filter((winner) => winner.userId === userId);
  const myPayout = myWin.reduce((sum, winner) => sum + winner.winnings, 0);
  const need = horsesNeeded(kind);
  const selected = picks.slice(0, need).filter((id): id is number => id !== null);
  const ready = selected.length === need && new Set(selected).size === need;
  const previewOdds = ready ? getBetOdds(table.odds, kind, selected) : undefined;
  const previewPay = previewOdds !== undefined ? koniePayout(chip, previewOdds, true) : null;
  const bettingOpen = seated && table.phase === "betting" && connected;
  const lastWinner = table.lastResults[0] ?? null;
  const podium = table.finishOrder ?? race?.finishOrder ?? null;

  useEffect(() => {
    setPicks(Array.from({ length: need }, () => null));
  }, [kind, need]);

  useEffect(() => {
    chatEnd.current?.scrollIntoView({ block: "end" });
  }, [chat.length]);

  useEffect(() => {
    if (!shopOpen && !helpOpen) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setShopOpen(false);
        setHelpOpen(false);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [shopOpen, helpOpen]);

  if (restoring) {
    return <p className="hub-hint">Ładowanie…</p>;
  }

  if (!user) {
    return (
      <Card title="Konie" icon="🐎">
        <p className="hub-hint">Zaloguj się w belce na górze, żeby wejść na tor.</p>
      </Card>
    );
  }

  function submitChat(event: FormEvent) {
    event.preventDefault();
    const text = chatDraft.trim();
    if (!text) return;
    sendChat(text);
    setChatDraft("");
  }

  function tapHorse(id: number) {
    setPicks((prev) => {
      const next = [...prev];
      if (need === 1) return [id];
      const existing = next.indexOf(id);
      if (existing >= 0) {
        next[existing] = null;
        return next;
      }
      const empty = next.findIndex((slot) => slot === null);
      if (empty >= 0) {
        next[empty] = id;
        return next;
      }
      next[need - 1] = id;
      return next;
    });
  }

  function onPlace() {
    if (!bettingOpen || !ready) return;
    placeBet(kind, selected, clampKnBet(chip));
  }

  const canClaim = (session?.canClaim ?? wallet?.canClaimBonus ?? false) && connected;
  const bonusLabel = canClaim
    ? `Bonus +${BJ.BONUS_AMOUNT}`
    : bonusLeft > 0
      ? `Bonus in ${formatHms(bonusLeft * 1000)}`
      : `Bonus +${BJ.BONUS_AMOUNT}`;
  const phaseLeft =
    table.phase === "betting" ? bettingLeft : table.phase === "racing" ? raceLeft : resultLeft;

  const rankBlock = (
    <>
      <h3>Ranking</h3>
      {board.error ? <p className="hub-error">{board.error}</p> : null}
      {board.rows.length === 0 && !board.error ? (
        <p className="hub-hint">Jeszcze nikt nie usiadł.</p>
      ) : (
        <ol className="kn-rank__list">
          {board.rows.map((row) => (
            <li key={row.username}>
              <span>{row.username}</span>
              <strong>{row.maxChips}</strong>
            </li>
          ))}
        </ol>
      )}
    </>
  );

  return (
    <>
    <div className="kn-page">
      {chatOpen ? (
        <button
          type="button"
          className="kn-chat-overlay"
          aria-label="Zamknij chat"
          onClick={() => setChatOpen(false)}
        />
      ) : null}

      <aside className={`kn-chat ${chatOpen ? "kn-chat--open" : ""}`}>
        <div className="kn-chat__head">
          <h3>Chat</h3>
          <button type="button" className="kn-chat__close" onClick={() => setChatOpen(false)}>
            ✕
          </button>
        </div>
        <section className="kn-rank kn-rank--side">{rankBlock}</section>
        <ol className="kn-history">
          {table.lastResults.length === 0 ? (
            <li className="hub-hint">Brak wyników</li>
          ) : (
            table.lastResults.map((row) => (
              <li key={row.raceNumber}>
                <span>#{row.raceNumber}</span>
                <strong>{row.winnerName}</strong>
              </li>
            ))
          )}
        </ol>
        <ul className="kn-chat__log">
          {chat.map((message, index) => {
            const mine = !message.isDealer && message.user === user.username;
            const lineClass = [
              "kn-chat__line",
              message.isDealer ? "kn-chat__line--dealer" : "",
              mine ? "kn-chat__line--mine" : "",
            ]
              .filter(Boolean)
              .join(" ");
            return (
              <li key={`${message.ts}-${index}`} className={lineClass}>
                <strong>{message.user}</strong>
                <span>{message.text}</span>
              </li>
            );
          })}
          <div ref={chatEnd} />
        </ul>
        <form className="kn-chat__form" onSubmit={submitChat}>
          <input
            value={chatDraft}
            onChange={(event) => setChatDraft(event.target.value)}
            maxLength={KN.MAX_CHAT_LENGTH}
            placeholder="Napisz…"
            autoComplete="off"
          />
          <Button type="submit" size="sm" variant="primary">
            Wyślij
          </Button>
        </form>
      </aside>

      <div className="kn-main">
        <div className="kn-toolbar">
          <div className="kn-hud">
            <div className="kn-hud__stat">
              Żetony: <strong>{fmtChips(chips)}</strong>
            </div>
            <div className="kn-hud__stat">
              Klucze: <strong>{keys}</strong>
            </div>
            <Button size="sm" variant="primary" className="kn-bonus" disabled={!canClaim} onClick={claimBonus}>
              {bonusLabel}
            </Button>
            <Button size="sm" onClick={() => setShopOpen(true)}>
              Sklep
            </Button>
            <Button size="sm" onClick={() => setHelpOpen(true)}>
              Pomoc
            </Button>
            {seated ? (
              <Button size="sm" onClick={leave}>
                Opuść tor
              </Button>
            ) : (
              <Button size="sm" variant="primary" disabled={!connected} onClick={join}>
                Dołącz
              </Button>
            )}
            <button
              type="button"
              className={`kn-rank-toggle ${rankOpen ? "kn-rank-toggle--on" : ""}`}
              onClick={() => setRankOpen((open) => !open)}
              aria-expanded={rankOpen}
            >
              🏆
            </button>
            <button type="button" className="kn-chat-toggle" onClick={() => setChatOpen(true)}>
              💬
            </button>
          </div>
        </div>

        <div className="kn-status">
          {notice ? <p className="kn-notice">{notice}</p> : null}
          {error ? <p className="hub-error">{error}</p> : null}
          {!connected && enabled ? <p className="hub-hint">Łączenie z torem…</p> : null}
        </div>

        <div className="kn-stage">
          <div className="kn-clock" data-phase={table.phase}>
            <span className="kn-clock__n">{phaseLeft > 0 ? `${phaseLeft}s` : "—"}</span>
            <span className="kn-clock__label">
              {table.phase === "waiting"
                ? "Oczekiwanie"
                : table.phase === "betting"
                  ? "Zakłady"
                  : table.phase === "racing"
                    ? "Wyścig"
                    : "Wynik"}
            </span>
          </div>
          <RaceTrack
            phase={table.phase}
            cue={race}
            finishOrder={table.finishOrder}
            lastWinner={lastWinner}
          />
        </div>

        <div className="kn-board">
          <section className="kn-panel kn-panel--field">
            <div className="kn-kinds">
              {KIND_TABS.map((tab) => (
                <button
                  key={tab}
                  type="button"
                  className={`kn-kind${kind === tab ? " kn-kind--on" : ""}`}
                  onClick={() => setKind(tab)}
                >
                  {BET_KIND_LABELS[tab]}
                </button>
              ))}
            </div>
            <p className="kn-kind-help">{BET_KIND_HELP[kind]}</p>
            {need > 1 ? (
              <div className="kn-slots">
                {Array.from({ length: need }, (_, index) => {
                  const id = picks[index];
                  const horse = id !== null && id !== undefined ? horseById(id) : undefined;
                  return (
                    <button
                      key={index}
                      type="button"
                      className="kn-slot-pick"
                      onClick={() =>
                        setPicks((prev) => {
                          const next = [...prev];
                          next[index] = null;
                          return next;
                        })
                      }
                    >
                      <span>{index + 1}.</span>
                      {horse ? (
                        <strong style={{ color: horse.color }}>{horse.name}</strong>
                      ) : (
                        <em>wybierz</em>
                      )}
                    </button>
                  );
                })}
              </div>
            ) : null}
            <div className="kn-horses">
              {HORSES.map((horse) => {
                const winOdds = table.odds.win[horse.id - 1];
                    const shown =
                      kind === "win" || kind === "place" || kind === "show"
                        ? table.odds[kind][horse.id - 1]
                        : winOdds;
                const on = picks.includes(horse.id);
                const wins = table.horseWins[horse.id - 1] ?? 0;
                return (
                  <button
                    key={horse.id}
                    type="button"
                    className={`kn-hcard${on ? " kn-hcard--on" : ""}`}
                    style={{ ["--kn-h" as string]: horse.color }}
                    onClick={() => tapHorse(horse.id)}
                    disabled={!bettingOpen && table.phase === "betting"}
                  >
                    <span className="kn-hcard__num" style={{ background: horse.color }}>
                      {horse.id}
                    </span>
                    <span className="kn-hcard__body">
                      <strong>{horse.name}</strong>
                      <em>{wins} wygr.</em>
                    </span>
                    <span className="kn-hcard__odds">{shown !== undefined ? `${shown.toFixed(1)}×` : "—"}</span>
                  </button>
                );
              })}
            </div>
          </section>

          <section className="kn-panel kn-panel--bet">
            <h3>Zakład</h3>
            <div className="kn-chips">
              {KN.CHIP_DENOMS.map((amount) => (
                <button
                  key={amount}
                  type="button"
                  className={`kn-chip${chip === amount ? " kn-chip--on" : ""}`}
                  onClick={() => setChip(amount)}
                >
                  {chipLabel(amount)}
                </button>
              ))}
            </div>
            <p className="kn-preview">
              {ready ? (
                <>
                  {formatKnBet(kind, selected)} · {chip} ż.
                  {previewOdds !== undefined ? ` · kurs ${previewOdds.toFixed(1)}×` : ""}
                  {previewPay !== null ? ` → ${previewPay}` : ""}
                </>
              ) : (
                "Wybierz konia i kwotę"
              )}
            </p>
            <div className="kn-bet-actions">
              <Button variant="primary" disabled={!bettingOpen || !ready} onClick={onPlace}>
                Postaw zakład
              </Button>
              <Button size="sm" disabled={!bettingOpen || myBets.length === 0} onClick={clearBets}>
                Wyczyść
              </Button>
            </div>
            {!seated ? <p className="hub-hint">Dołącz, żeby stawiać.</p> : null}
            {seated && table.phase !== "betting" ? (
              <p className="hub-hint">Poczekaj na otwarcie zakładów.</p>
            ) : null}
            <h3>Twoje zakłady</h3>
            {myBets.length === 0 ? (
              <p className="hub-hint">Brak</p>
            ) : (
              <ul className="kn-bets">
                {myBets.map((bet) => (
                  <li key={bet.id}>
                    <span>{formatKnBet(bet.kind, bet.horses)}</span>
                    <strong>
                      {bet.amount} · {bet.odds.toFixed(1)}×
                    </strong>
                  </li>
                ))}
              </ul>
            )}
          </section>

          <section className="kn-panel">
            <h3>Na trybunach</h3>
            {table.players.length === 0 ? (
              <p className="hub-hint">Nikogo nie ma</p>
            ) : (
              <ul className="kn-players">
                {table.players.map((player) => (
                  <li key={player.userId}>
                    <span className="kn-swatch" style={{ background: player.color }} />
                    <span>{player.name}</span>
                    <strong>{fmtChips(player.chips)}</strong>
                  </li>
                ))}
              </ul>
            )}
            {table.phase === "results" && podium ? (
              <Results
                order={podium}
                myBets={myBets}
                myPayout={myPayout}
                seated={seated}
              />
            ) : null}
          </section>
        </div>

        <div className={`kn-rank kn-rank--overlay ${rankOpen ? "kn-rank--open" : ""}`}>{rankBlock}</div>
      </div>

      {helpOpen ? <KonieHelp onClose={() => setHelpOpen(false)} /> : null}
      {shopOpen ? (
        <div
          className="shop-modal-root"
          onClick={(event) => {
            if (event.target === event.currentTarget) setShopOpen(false);
          }}
        >
          <ShopPanel variant="modal" onClose={() => setShopOpen(false)} initialChips={chips} initialKeys={keys} />
        </div>
      ) : null}
    </div>
    <CasinoOnPageAdmin onChanged={() => setAdminTick((n) => n + 1)} />
    </>
  );
}

function Results({
  order,
  myBets,
  myPayout,
  seated,
}: {
  order: readonly number[];
  myBets: readonly KnPublicBet[];
  myPayout: number;
  seated: boolean;
}) {
  return (
    <div className="kn-results">
      <ol className="kn-podium">
        {order.slice(0, 3).map((id, index) => {
          const horse = horseById(id);
          return (
            <li key={id} className={`kn-podium__n kn-podium__n--${index + 1}`}>
              <span>{index + 1}.</span>
              <strong style={{ color: horse?.color }}>{horse?.name ?? `#${id}`}</strong>
            </li>
          );
        })}
      </ol>
      {!seated ? null : myBets.length === 0 ? (
        <p>Nie grałeś w tym wyścigu.</p>
      ) : (
        <>
          <ul className="kn-settle">
            {myBets.map((bet) => {
              const won = isKnBetWinner(bet.kind, bet.horses, order);
              const pay = koniePayout(bet.amount, bet.odds, won);
              return (
                <li key={bet.id} className={won ? "kn-settle--win" : "kn-settle--lose"}>
                  <span>{formatKnBet(bet.kind, bet.horses)}</span>
                  <strong>{won ? `+${pay}` : `−${bet.amount}`}</strong>
                </li>
              );
            })}
          </ul>
          <p className="kn-results__total">
            {myPayout > 0 ? `Wypłata: ${myPayout}` : "Tym razem pudło."}
          </p>
        </>
      )}
    </div>
  );
}
